/**
 * AgentWorkshop — refine-before-execute space inside HQ.
 *
 * Layout (v3 — Build / Package / Sell trinity, 3 folder tabs + per-task chat):
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │  Folder tabs · Aria (Build) · Lyra (Package) · Hermes (Sell) │
 *   ├──────────────────┬───────────────────────────────────────────┤
 *   │ Tasks rail (l)   │  Visual preview (compact)                 │
 *   │ + promote btn    │  ─────                                    │
 *   │   on each card   │  Chat history with the agent              │
 *   │                  │  (questions + answers, task-scoped)       │
 *   │                  │  ─────                                    │
 *   │                  │  [input ────────] [send]                  │
 *   └──────────────────┴───────────────────────────────────────────┘
 *
 *   · Hermes now owns the SELL pillar end-to-end — the legacy Markus folder
 *     was absorbed (lead-gen + outreach + closing are one unified agent).
 *   · Folder tabs replace the previous "Back to office" CTA; the parent
 *     HQ switcher already lets the user jump out of the Workshop.
 *   · "Promote to real" lives on every task card in the rail — no more
 *     sticky bar in the right pane.
 *   · The right pane is now a live conversation: user → agent reply
 *     (POST /api/hq/tasks/{id}/chat via LLM gateway).
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  CheckCircle2, Loader2, Send, Sparkles, AlertOctagon, Clock,
} from 'lucide-react';
import { AriaPreview, HermesPreview, LyraPreview, EmptyPreview } from './AgentPreviews';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Build / Package / Sell trinity. Hermes absorbed the former Markus pillar
// (lead-gen + outreach + closing are now one unified SELL agent), so every
// legacy markus/marcus/revenue/sales task surfaces inside the Hermes folder.
const AGENT_FILTERS = {
  builder: { match: ['builder', 'aria'],   name: 'Aria',   role: 'The Architect',  Preview: AriaPreview,
             accent: '#7C3AED', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
             img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/oqa19gtc_77de9bac-3f25-4df6-83c9-1bfd45a5acfe.png' },
  lyra:    { match: ['lyra', 'content'],   name: 'Lyra',   role: 'The Storyteller', Preview: LyraPreview,
             accent: '#FF6B1A', gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)',
             img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/a3f3fe57_2f141ba8-6ea7-4405-9fd5-d1d34fa09a9f.png' },
  hermes:  { match: ['hermes', 'growth', 'markus', 'marcus', 'revenue', 'sales'],
             name: 'Hermes', role: 'The Pathfinder', Preview: HermesPreview,
             accent: '#FFB347', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)',
             img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/mou7csvz_238e23e8-fd0c-4b2b-ae70-8b1eb646daec.png' },
};

const AGENT_ORDER = ['builder', 'lyra', 'hermes'];

export default function AgentWorkshop({ hq, agentKey, onAgentChange, onTaskChanged, preselectTaskId }) {
  // Collapse legacy aliases (markus/marcus/revenue/sales/growth) to hermes
  // so navigation from old links lands on the unified SELL folder.
  const resolvedAgentKey = (() => {
    if (AGENT_FILTERS[agentKey]) return agentKey;
    const lower = (agentKey || '').toLowerCase();
    if (['markus', 'marcus', 'revenue', 'sales', 'growth'].includes(lower)) return 'hermes';
    if (['aria'].includes(lower)) return 'builder';
    if (['content'].includes(lower)) return 'lyra';
    return 'builder';
  })();
  const agent = AGENT_FILTERS[resolvedAgentKey];

  // Per-agent task counts for the folder tabs.
  const allTasks = hq?.tasks || [];
  const tasksByAgent = useMemo(() => {
    const out = { builder: [], lyra: [], hermes: [] };
    for (const t of allTasks) {
      const owner = (t.owner_agent_id || '').toLowerCase();
      for (const k of AGENT_ORDER) {
        if (AGENT_FILTERS[k].match.includes(owner)) {
          out[k].push(t);
          break;
        }
      }
    }
    return out;
  }, [allTasks]);

  return (
    <div data-testid="agent-workshop" style={{
      flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #FAF8F2 0%, #F1EEE6 100%)',
      overflow: 'hidden',
    }}>
      <FolderTabs
        active={resolvedAgentKey}
        onChange={onAgentChange}
        tasksByAgent={tasksByAgent}
      />
      <AgentPane
        key={resolvedAgentKey}
        agent={agent}
        agentKey={resolvedAgentKey}
        hq={hq}
        tasks={tasksByAgent[resolvedAgentKey] || []}
        onTaskChanged={onTaskChanged}
        preselectTaskId={preselectTaskId}
      />
    </div>
  );
}

/* ============================ Folder tabs ============================ */
function FolderTabs({ active, onChange, tasksByAgent }) {
  return (
    <div style={{
      flexShrink: 0,
      paddingTop: 16, paddingLeft: 28, paddingRight: 28,
      background: '#FFFFFF',
      borderBottom: '1px solid rgba(15,15,30,0.08)',
      position: 'relative',
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-end', gap: 2,
      }}>
        {AGENT_ORDER.map((key) => {
          const cfg = AGENT_FILTERS[key];
          const isActive = key === active;
          const count = (tasksByAgent[key] || []).length;
          return (
            <FolderTab
              key={key}
              cfg={cfg}
              count={count}
              active={isActive}
              testid={`workshop-tab-${key}`}
              onClick={() => onChange && onChange(key)}
            />
          );
        })}
        <div style={{ flex: 1, height: 1, borderBottom: '1px solid rgba(15,15,30,0.08)' }} />
      </div>
    </div>
  );
}

function FolderTab({ cfg, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{
        position: 'relative',
        cursor: 'pointer', fontFamily: 'inherit',
        padding: '12px 22px 14px 18px',
        marginRight: -10,
        background: active
          ? `linear-gradient(180deg, #FFFFFF 0%, ${cfg.accent}10 100%)`
          : 'rgba(15,15,30,0.025)',
        border: 'none',
        borderTop: `1px solid ${active ? `${cfg.accent}55` : 'rgba(15,15,30,0.08)'}`,
        borderLeft: `1px solid ${active ? `${cfg.accent}55` : 'rgba(15,15,30,0.08)'}`,
        borderRight: `1px solid ${active ? `${cfg.accent}55` : 'rgba(15,15,30,0.08)'}`,
        borderTopLeftRadius: 14, borderTopRightRadius: 14,
        clipPath: 'polygon(14px 0, calc(100% - 14px) 0, 100% 100%, 0 100%)',
        color: '#0F0F1A',
        zIndex: active ? 5 : 1,
        transform: active ? 'translateY(1px)' : 'translateY(0)',
        transition: 'all 0.18s',
        minWidth: 152,
        display: 'flex', alignItems: 'center', gap: 10,
        boxShadow: active ? `0 -2px 16px ${cfg.accent}22` : 'none',
      }}
      onMouseEnter={e => {
        if (!active) e.currentTarget.style.background = 'rgba(15,15,30,0.05)';
      }}
      onMouseLeave={e => {
        if (!active) e.currentTarget.style.background = 'rgba(15,15,30,0.025)';
      }}
    >
      <span style={{
        width: 9, height: 9, borderRadius: '50%',
        background: cfg.accent,
        boxShadow: active ? `0 0 10px ${cfg.accent}88` : `0 0 0 2px ${cfg.accent}33`,
        flexShrink: 0,
      }} />
      <span style={{
        fontSize: 12.5,
        fontWeight: active ? 900 : 700,
        letterSpacing: '-0.005em',
        color: active ? cfg.accent : 'rgba(15,15,30,0.7)',
        whiteSpace: 'nowrap',
      }}>
        {cfg.name}
      </span>
      <span style={{
        marginLeft: 'auto',
        fontSize: 10, fontWeight: 800,
        fontFamily: "'JetBrains Mono', monospace",
        color: active ? cfg.accent : 'rgba(15,15,30,0.4)',
        padding: '2px 7px',
        background: active ? `${cfg.accent}18` : 'rgba(15,15,30,0.05)',
        borderRadius: 999,
      }}>
        {count}
      </span>
      {active && (
        <span style={{
          position: 'absolute', bottom: -1, left: 14, right: 14,
          height: 2,
          background: cfg.gradient,
          borderRadius: 2,
        }} />
      )}
    </button>
  );
}

/* ============================ Agent pane ============================ */
function AgentPane({ agent, agentKey, hq, tasks, onTaskChanged, preselectTaskId }) {
  // Tasks already filtered, sort with active first
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const sa = a.status === 'done' ? 3 : a.status === 'blocked' ? 0 : a.status === 'in_progress' ? 1 : 2;
      const sb = b.status === 'done' ? 3 : b.status === 'blocked' ? 0 : b.status === 'in_progress' ? 1 : 2;
      if (sa !== sb) return sa - sb;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [tasks]);

  // Default to the deep-link target when arriving from Execution → workshop;
  // fall back to the first task in the rail if the preselected id isn't here
  // (e.g. wrong agent folder).
  const [selectedId, setSelectedId] = useState(() => {
    if (preselectTaskId && sortedTasks.some(t => t.task_id === preselectTaskId)) {
      return preselectTaskId;
    }
    return sortedTasks[0]?.task_id || null;
  });
  // Honour later preselect updates (e.g. founder navigates back & forth).
  useEffect(() => {
    if (preselectTaskId && sortedTasks.some(t => t.task_id === preselectTaskId)) {
      setSelectedId(preselectTaskId);
    }
  }, [preselectTaskId, sortedTasks]);
  const selected = sortedTasks.find(t => t.task_id === selectedId) || null;

  return (
    <div style={{
      flex: 1, minHeight: 0,
      display: 'grid',
      gridTemplateColumns: 'minmax(280px, 340px) 1fr',
      gap: 0,
    }}>
      <TaskRail
        tasks={sortedTasks}
        selectedId={selectedId}
        onSelect={setSelectedId}
        agent={agent}
        onTaskChanged={onTaskChanged}
      />
      <ChatPane
        key={selected?.task_id || 'empty'}
        agent={agent}
        agentKey={agentKey}
        task={selected}
        hq={hq}
        onTaskChanged={onTaskChanged}
      />
    </div>
  );
}

/* ============================ Task rail ============================ */
function TaskRail({ tasks, selectedId, onSelect, agent, onTaskChanged }) {
  return (
    <div style={{
      borderRight: '1px solid rgba(15,15,30,0.06)',
      background: 'rgba(255,255,255,0.55)',
      display: 'flex', flexDirection: 'column',
      minHeight: 0,
    }}>
      <div style={{
        padding: '14px 18px 10px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: '1px solid rgba(15,15,30,0.05)',
      }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: 'rgba(15,15,30,0.6)',
        }}>
          {agent.name}&apos;s queue
        </span>
        <span style={{
          fontSize: 9, fontWeight: 800,
          padding: '2px 7px', borderRadius: 999,
          background: `${agent.accent}18`,
          color: agent.accent,
          fontFamily: "'JetBrains Mono', monospace",
          marginLeft: 'auto',
        }}>
          {tasks.length}
        </span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tasks.length === 0 ? (
          <div style={{
            padding: '40px 16px', textAlign: 'center',
            color: 'rgba(15,15,30,0.45)', fontSize: 12,
            lineHeight: 1.5,
          }}>
            No tasks queued for {agent.name} yet.<br />
            <span style={{ fontSize: 11, color: 'rgba(15,15,30,0.35)' }}>
              Generate a daily brief or run an orchestration cycle.
            </span>
          </div>
        ) : (
          tasks.map(t => (
            <TaskRow
              key={t.task_id}
              task={t}
              active={t.task_id === selectedId}
              onClick={() => onSelect(t.task_id)}
              agent={agent}
              onTaskChanged={onTaskChanged}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, active, onClick, agent, onTaskChanged }) {
  const refined = !!task.refined;
  const isBlocked = task.status === 'blocked';
  const isDone = task.status === 'done';
  const [promoting, setPromoting] = useState(false);

  const promote = async (e) => {
    e.stopPropagation();
    if (promoting || refined) return;
    setPromoting(true);
    try {
      const r = await fetch(`${API}/api/hq/tasks/${task.task_id}/refine`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: task.title,
          description: task.description || '',
          refinement_notes: null,
        }),
      });
      if (r.ok && onTaskChanged) onTaskChanged();
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div
      data-testid={`workshop-task-${task.task_id}`}
      onClick={onClick}
      style={{
        textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
        padding: 12,
        background: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
        border: `1px solid ${active ? `${agent.accent}55` : 'rgba(15,15,30,0.06)'}`,
        borderLeft: `3px solid ${active ? agent.accent : 'transparent'}`,
        borderRadius: 10,
        color: '#0F0F1A',
        boxShadow: active ? `0 8px 24px ${agent.accent}22` : 'none',
        transition: 'all 0.15s',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#FFFFFF'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 8, padding: '2px 5px', fontWeight: 800,
          background: task.priority === 'P0' ? '#FF6B6B' : task.priority === 'P1' ? agent.accent : 'rgba(15,15,30,0.08)',
          color: task.priority === 'P0' ? '#fff' : task.priority === 'P1' ? '#fff' : 'rgba(15,15,30,0.72)',
          fontFamily: "'JetBrains Mono', monospace",
          borderRadius: 4,
        }}>{task.priority || 'P2'}</span>

        {refined && (
          <span style={{
            fontSize: 8, padding: '2px 6px', fontWeight: 800,
            background: 'rgba(34,197,94,0.18)', color: '#15803D',
            border: '1px solid rgba(34,197,94,0.4)',
            borderRadius: 4,
            letterSpacing: '0.08em',
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <Sparkles size={9} /> REFINED
          </span>
        )}
        {isBlocked && (
          <span style={{
            fontSize: 8, padding: '2px 5px', fontWeight: 800,
            background: 'rgba(255,107,107,0.18)', color: '#FF6B6B',
            border: '1px solid rgba(255,107,107,0.4)', borderRadius: 4,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <AlertOctagon size={9} /> BLOCKED
          </span>
        )}
        {isDone && (
          <span style={{
            fontSize: 8, padding: '2px 5px', fontWeight: 800,
            background: 'rgba(34,197,94,0.18)', color: '#15803D',
            border: '1px solid rgba(34,197,94,0.4)', borderRadius: 4,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <CheckCircle2 size={9} /> SHIPPED
          </span>
        )}
      </div>

      <div style={{
        fontSize: 12.5, fontWeight: 700,
        color: '#0F0F1A',
        lineHeight: 1.35,
      }}>
        {task.title}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginTop: 2,
      }}>
        <div style={{
          fontSize: 10, color: 'rgba(15,15,30,0.5)',
          fontFamily: "'JetBrains Mono', monospace",
          display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0,
        }}>
          <Clock size={9} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {task.eta_minutes ? `${task.eta_minutes}m` : task.status}
            {task.tool ? ` · ${(task.tool || '').split('.')[0]}` : ''}
          </span>
        </div>
        <button
          onClick={promote}
          disabled={promoting || refined}
          data-testid={`workshop-promote-${task.task_id}`}
          style={{
            padding: '5px 11px',
            background: refined
              ? 'rgba(34,197,94,0.12)'
              : promoting
              ? 'rgba(15,15,30,0.08)'
              : agent.gradient,
            color: refined ? '#15803D' : promoting ? 'rgba(15,15,30,0.45)' : '#FFFFFF',
            border: refined ? '1px solid rgba(34,197,94,0.3)' : 'none',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: refined || promoting ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 5,
            boxShadow: refined || promoting ? 'none' : `0 4px 12px ${agent.accent}55`,
            flexShrink: 0,
            transition: 'all 0.15s',
          }}
        >
          {promoting ? (
            <Loader2 size={10} style={{ animation: 'spin 0.7s linear infinite' }} />
          ) : refined ? (
            <><CheckCircle2 size={10} /> Promoted</>
          ) : (
            <><Send size={10} /> Promote</>
          )}
        </button>
      </div>
    </div>
  );
}

/* ============================ Chat pane ============================ */
function ChatPane({ agent, agentKey, task, hq, onTaskChanged }) {
  const Preview = agent.Preview;
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Filter chat messages for the selected task
  const taskMessages = useMemo(() => {
    if (!task) return [];
    const all = hq?.messages || [];
    return all
      .filter(m => m.task_id === task.task_id && (m.kind === 'workshop_chat' || m.kind === 'delivery' || m.kind === 'refine'))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [task, hq?.messages]);

  // Auto-scroll the chat to the latest message whenever the list grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [taskMessages.length, sending]);

  if (!task) {
    return (
      <div style={{
        padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <EmptyPreview agent={agent} />
      </div>
    );
  }

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setDraft('');
    try {
      const r = await fetch(`${API}/api/hq/tasks/${task.task_id}/chat`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, agent_key: agentKey }),
      });
      if (r.ok && onTaskChanged) onTaskChanged();
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      minHeight: 0, overflow: 'hidden',
    }}>
      {/* Task title header */}
      <div style={{
        flexShrink: 0,
        padding: '14px 28px 12px',
        borderBottom: '1px solid rgba(15,15,30,0.06)',
        background: '#FFFFFF',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: agent.accent, marginBottom: 4,
          }}>
            Now shaping
          </div>
          <div style={{
            fontSize: 17, fontWeight: 800, color: '#0F0F1A',
            letterSpacing: '-0.01em', lineHeight: 1.3,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {task.title}
          </div>
        </div>
        {task.refined && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 9, fontWeight: 800,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(34,197,94,0.14)', color: '#15803D',
            border: '1px solid rgba(34,197,94,0.3)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            <Sparkles size={10} /> Refined
          </span>
        )}
      </div>

      {/* Scrollable area: preview + messages */}
      <div ref={scrollRef} style={{
        flex: 1, minHeight: 0, overflowY: 'auto',
        padding: 24,
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        {/* Live artifact preview — when the recipe has produced a deliverable
            (5 IG posts, copy variants, a step-by-step plan, or per-recipient
            DMs from a parent batch), render it as preview CARDS here so the
            founder SEES the work, not just the agent's commentary. Children
            of parent batches are pulled in too via `relatedTasks`. */}
        <ArtifactCards
          task={task}
          relatedTasks={(hq?.tasks || []).filter(t => t.parent_task_id === task.task_id)}
          agent={agent}
          onTaskChanged={onTaskChanged}
        />

        {/* Preview */}
        <div>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(15,15,30,0.5)',
            display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10,
          }}>
            <span style={{ width: 18, height: 1, background: agent.accent }} />
            Visual preview · {agent.name}
          </div>
          <Preview task={task} />
        </div>

        {/* Chat history */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: 'rgba(15,15,30,0.5)',
            display: 'flex', alignItems: 'center', gap: 7,
          }}>
            <span style={{ width: 18, height: 1, background: agent.accent }} />
            Conversation with {agent.name}
          </div>
          {taskMessages.length === 0 ? (
            <div style={{
              padding: '20px 16px',
              background: `${agent.accent}08`,
              border: `1px dashed ${agent.accent}38`,
              borderRadius: 12,
              fontSize: 12, color: 'rgba(15,15,30,0.6)',
              textAlign: 'center', lineHeight: 1.55,
            }}>
              Ask {agent.name} anything — sharpen the hook, propose alternatives,
              challenge the brief, or request a different angle.
            </div>
          ) : (
            taskMessages.map(m => (
              <ChatBubble key={m.message_id} message={m} agent={agent} />
            ))
          )}
          {sending && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', alignSelf: 'flex-start',
              background: `${agent.accent}10`,
              border: `1px solid ${agent.accent}30`,
              borderRadius: 14,
              fontSize: 11, color: agent.accent, fontWeight: 700,
            }}>
              <Loader2 size={12} style={{ animation: 'spin 0.7s linear infinite' }} />
              {agent.name} is thinking…
            </div>
          )}
        </div>
      </div>

      {/* Chat input */}
      <div style={{
        flexShrink: 0,
        padding: '14px 24px',
        background: '#FFFFFF',
        borderTop: '1px solid rgba(15,15,30,0.08)',
        display: 'flex', gap: 10, alignItems: 'flex-end',
      }}>
        <textarea
          data-testid="workshop-chat-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Talk to ${agent.name} about this task — what should change?`}
          rows={1}
          style={{
            flex: 1, padding: '11px 14px',
            fontSize: 13, color: '#0F0F1A', lineHeight: 1.5,
            fontFamily: 'inherit',
            border: '1px solid rgba(15,15,30,0.12)',
            borderRadius: 12,
            outline: 'none',
            background: '#FBFAF7',
            resize: 'none',
            maxHeight: 140,
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onFocus={e => { e.target.style.borderColor = agent.accent; e.target.style.boxShadow = `0 0 0 3px ${agent.accent}25`; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(15,15,30,0.12)'; e.target.style.boxShadow = 'none'; }}
        />
        <button
          onClick={send}
          disabled={sending || !draft.trim()}
          data-testid="workshop-chat-send"
          style={{
            padding: '11px 18px',
            background: draft.trim() && !sending ? agent.gradient : 'rgba(15,15,30,0.06)',
            color: draft.trim() && !sending ? '#FFFFFF' : 'rgba(15,15,30,0.4)',
            border: 'none', borderRadius: 12,
            fontFamily: 'inherit',
            fontSize: 12, fontWeight: 800, letterSpacing: '0.04em',
            cursor: draft.trim() && !sending ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', gap: 7,
            boxShadow: draft.trim() && !sending ? `0 10px 22px ${agent.accent}55` : 'none',
            transition: 'all 0.15s',
          }}
        >
          {sending ? (
            <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} />
          ) : (
            <><Send size={13} /> Send</>
          )}
        </button>
      </div>
    </div>
  );
}

function ChatBubble({ message, agent }) {
  const isUser = message.from_kind === 'user';
  const isDelivery = message.kind === 'delivery';
  const isRefine = message.kind === 'refine';

  if (isRefine) {
    return (
      <div style={{
        alignSelf: 'center',
        padding: '6px 12px',
        fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'rgba(15,15,30,0.5)',
        background: 'rgba(15,15,30,0.04)',
        borderRadius: 999,
      }}>
        — task refined —
      </div>
    );
  }

  return (
    <div data-testid={`workshop-msg-${message.message_id}`} style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      gap: 10,
    }}>
      {!isUser && (
        <div style={{
          width: 30, height: 30, flexShrink: 0,
          borderRadius: '50%', overflow: 'hidden',
          boxShadow: `0 4px 12px ${agent.accent}38, 0 0 0 1.5px ${agent.accent}55`,
        }}>
          <img src={agent.img} alt={agent.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
        </div>
      )}
      <div style={{
        maxWidth: '74%',
        padding: '10px 14px',
        background: isUser
          ? 'linear-gradient(135deg, #0F0F1A 0%, #1F1F2E 100%)'
          : isDelivery
          ? `linear-gradient(135deg, ${agent.accent}18 0%, ${agent.accent}05 100%)`
          : '#FFFFFF',
        color: isUser ? '#F5F5F7' : '#0F0F1A',
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        border: isUser ? 'none' : isDelivery
          ? `1px solid ${agent.accent}38`
          : '1px solid rgba(15,15,30,0.07)',
        boxShadow: isUser ? '0 6px 16px rgba(15,15,30,0.18)' : '0 2px 8px rgba(15,15,30,0.05)',
      }}>
        {!isUser && (
          <div style={{
            fontSize: 10, fontWeight: 800,
            color: agent.accent,
            letterSpacing: '0.06em',
            marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {isDelivery && <span style={{
              fontSize: 8, padding: '1px 5px', borderRadius: 3,
              background: agent.accent, color: '#FFFFFF',
              letterSpacing: '0.12em',
            }}>DELIVERY</span>}
            {agent.name}
          </div>
        )}
        <div style={{
          fontSize: 12.5, lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}>
          {message.message}
        </div>
      </div>
    </div>
  );
}


/* ============================ Artifact preview ============================
   Renders the live deliverable so the founder sees what the recipe produced
   (NOT just the agent's commentary):

     · Hermes outreach_batch  → header + per-recipient children rendered as
                                individual ship-ready cards (Phase 1 magic).
     · Hermes outreach_item   → single recipient card (when the task IS a child).
     · Lyra content_draft     → post variants / captions / brand copy.
     · Aria plan/code         → numbered steps + acceptance criteria + optional
                                live HTML/CSS preview iframe.

   Each card is data-testid'd and the Hermes child cards expose a Ship CTA
   that hits POST /api/tasks/{id}/validate?action=approve — the same path the
   Validation Modal uses, so no new endpoints. */

function ArtifactCards({ task, relatedTasks, agent, onTaskChanged }) {
  if (!task) return null;
  const artifact = task.artifact;
  const hasChildren = Array.isArray(relatedTasks) && relatedTasks.length > 0;
  if (!artifact && !hasChildren) return null;

  const kind = artifact?.kind;
  const content = artifact?.content || {};

  return (
    <div data-testid="workshop-artifact-cards" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ width: 18, height: 1, background: agent.accent }} />
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: agent.accent,
        }}>
          Live deliverable · {agent.name} built this
        </span>
      </div>

      {kind === 'outreach_batch' && (
        <OutreachBatchSummary content={content} agent={agent} childrenCount={relatedTasks?.length || 0} />
      )}
      {kind === 'outreach_item' && (
        <OutreachItemCard task={task} content={content} agent={agent} onTaskChanged={onTaskChanged} />
      )}
      {kind === 'content_draft' && (
        <ContentDraftCards content={content} agent={agent} />
      )}
      {(kind === 'plan' || kind === 'execution_plan') && (
        <PlanCard content={content} agent={agent} />
      )}

      {/* Per-child cards when the task is a parent that spawned recipients. */}
      {hasChildren && (
        <div data-testid="workshop-children-rail" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'rgba(15,15,30,0.55)',
            marginTop: 4,
          }}>
            {relatedTasks.length} ship-ready deliverable{relatedTasks.length > 1 ? 's' : ''}
          </div>
          {relatedTasks.map(child => (
            <OutreachItemCard
              key={child.task_id}
              task={child}
              content={child.artifact?.content || {}}
              agent={agent}
              onTaskChanged={onTaskChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OutreachBatchSummary({ content, agent, childrenCount }) {
  const channel = (content.channel || 'outreach').toUpperCase();
  return (
    <div style={{
      padding: 14,
      background: `${agent.accent}08`,
      border: `1px solid ${agent.accent}33`,
      borderRadius: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999,
          background: agent.accent, color: '#FFFFFF', letterSpacing: '0.14em',
        }}>{channel}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A' }}>
          {childrenCount > 0 ? `${childrenCount} recipient${childrenCount > 1 ? 's' : ''} drafted` : 'Batch composed'}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'rgba(15,15,30,0.65)', lineHeight: 1.55, marginTop: 4 }}>
        {(content.message_template || '').slice(0, 220)}
        {(content.message_template || '').length > 220 && '…'}
      </div>
    </div>
  );
}

function OutreachItemCard({ task, content, agent, onTaskChanged }) {
  const recipient = content.recipient || {};
  const handle = recipient.handle || recipient.email || recipient.company_domain || recipient.name || 'recipient';
  const why = recipient.why_fit;
  const state = task.current_state;
  const isShipped = state === 'done' || task.status === 'done';
  const isReady = state === 'review';

  const [shipping, setShipping] = useState(false);
  const ship = async (e) => {
    e?.stopPropagation();
    if (shipping || isShipped) return;
    setShipping(true);
    try {
      await fetch(`${API}/api/tasks/${task.task_id}/validate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve', notes: '' }),
      });
      onTaskChanged && onTaskChanged();
    } finally {
      setShipping(false);
    }
  };

  return (
    <div data-testid={`workshop-outreach-card-${task.task_id}`} style={{
      padding: 14,
      background: '#FFFFFF',
      border: `1px solid ${isShipped ? '#22C55E55' : isReady ? agent.accent + '55' : 'rgba(15,15,30,0.08)'}`,
      borderLeft: `3px solid ${isShipped ? '#22C55E' : agent.accent}`,
      borderRadius: 12,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999,
          background: `${agent.accent}18`, color: agent.accent, letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}>
          {(content.channel || 'channel').toUpperCase()}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: '#0F0F1A', flex: 1, minWidth: 0,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {handle}
        </span>
        {isShipped ? (
          <span style={{
            fontSize: 9, fontWeight: 800, padding: '4px 9px', borderRadius: 999,
            background: 'rgba(34,197,94,0.18)', color: '#15803D',
            border: '1px solid rgba(34,197,94,0.4)',
            letterSpacing: '0.14em', textTransform: 'uppercase',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}><CheckCircle2 size={10} /> Shipped</span>
        ) : (
          <button
            onClick={ship}
            disabled={shipping || !isReady}
            data-testid={`workshop-outreach-ship-${task.task_id}`}
            style={{
              padding: '5px 12px', borderRadius: 999,
              background: isReady ? agent.gradient : 'rgba(15,15,30,0.08)',
              color: isReady ? '#FFFFFF' : 'rgba(15,15,30,0.45)',
              fontFamily: 'inherit', fontWeight: 800, fontSize: 9.5,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              border: 'none',
              cursor: isReady && !shipping ? 'pointer' : 'not-allowed',
              boxShadow: isReady ? `0 4px 12px ${agent.accent}40` : 'none',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            {shipping ? <Loader2 size={10} style={{ animation: 'spin 0.7s linear infinite' }} /> : <Send size={10} />}
            {shipping ? 'Sending…' : isReady ? 'Ship' : (state || '…')}
          </button>
        )}
      </div>
      {why && (
        <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)', fontStyle: 'italic' }}>
          {why.slice(0, 160)}
        </div>
      )}
      <div style={{
        padding: 11, background: '#FBFAF7',
        border: '1px solid rgba(15,15,30,0.06)', borderRadius: 9,
        fontSize: 12, color: '#0F0F1A', lineHeight: 1.55,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {(content.message || '').slice(0, 600)}
      </div>
    </div>
  );
}

function ContentDraftCards({ content, agent }) {
  const variants = content.variants || content.posts || [];
  if (!variants.length && content.body) {
    return (
      <div style={{
        padding: 14, background: '#FFFFFF',
        border: `1px solid ${agent.accent}33`, borderLeft: `3px solid ${agent.accent}`,
        borderRadius: 12, fontSize: 12.5, color: '#0F0F1A', whiteSpace: 'pre-wrap',
      }}>
        {content.body}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {variants.map((v, i) => (
        <div key={i} data-testid={`workshop-content-card-${i}`} style={{
          padding: 14, background: '#FFFFFF',
          border: `1px solid ${agent.accent}33`, borderLeft: `3px solid ${agent.accent}`,
          borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.14em',
            color: agent.accent, textTransform: 'uppercase',
          }}>
            Variant {i + 1}{v.hook ? ` · ${v.hook.slice(0, 40)}` : ''}
          </div>
          <div style={{ fontSize: 12.5, color: '#0F0F1A', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
            {v.body || v.text || v.caption || v.content || JSON.stringify(v)}
          </div>
        </div>
      ))}
    </div>
  );
}

function PlanCard({ content, agent }) {
  const steps = content.steps || content.plan || [];
  const accept = content.acceptance_criteria || content.acceptance || [];
  return (
    <div style={{
      padding: 14, background: '#FFFFFF',
      border: `1px solid ${agent.accent}33`, borderLeft: `3px solid ${agent.accent}`,
      borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {steps.length > 0 && (
        <div>
          <div style={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.18em',
            color: agent.accent, textTransform: 'uppercase', marginBottom: 6,
          }}>Plan</div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: '#0F0F1A', lineHeight: 1.6 }}>
            {steps.slice(0, 12).map((s, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {typeof s === 'string' ? s : (s.text || s.title || JSON.stringify(s))}
              </li>
            ))}
          </ol>
        </div>
      )}
      {accept.length > 0 && (
        <div>
          <div style={{
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.18em',
            color: 'rgba(15,15,30,0.55)', textTransform: 'uppercase', marginBottom: 6,
          }}>Acceptance criteria</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: 'rgba(15,15,30,0.7)', lineHeight: 1.55 }}>
            {accept.slice(0, 8).map((a, i) => (
              <li key={i} style={{ marginBottom: 3 }}>
                {typeof a === 'string' ? a : (a.text || JSON.stringify(a))}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Phase 2: when Aria embeds an HTML/CSS preview, render it sandboxed
          so the founder previews the actual UI build live in her chat. */}
      {content.html_preview && (
        <iframe
          data-testid="workshop-aria-html-preview"
          title="Aria HTML preview"
          srcDoc={String(content.html_preview).slice(0, 80000)}
          sandbox=""
          style={{
            width: '100%', minHeight: 240, border: '1px solid rgba(15,15,30,0.1)',
            borderRadius: 10, background: '#FFFFFF', marginTop: 4,
          }}
        />
      )}
    </div>
  );
}
